import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  useParams,
} from "react-router-dom";
import './App.css';
import { HelmetProvider } from 'react-helmet-async';
import React, { lazy, Suspense, useEffect, useState } from 'react';
import ErrorBoundary from "./components/ErrorBoundary/ErrorBoundary";
import SellerLoader from "./components/Loader/SellerLoader";
import { resolveSellerID, isCustomDomain, getCurrentDomain } from "./utils/domainResolver";
import MetaPixelBootstrap from "./components/MetaPixel/MetaPixelBootstrap";
import WebAnalyticsBootstrap from "./components/WebAnalytics/WebAnalyticsBootstrap";

// Lazy load all store pages
const Store = lazy(() => import('./pages/Store/Store'));
const CategoryPage = lazy(() => import('./pages/Store/CategoryPage'));
const PolicyPage = lazy(() => import('./pages/Store/PolicyPage'));
const NewCustomerPage = lazy(() => import('./pages/Store/NewCustomerPage'));
const CheckoutPage = lazy(() => import('./pages/Store/CheckoutPage'));
const PaymentSuccessPage = lazy(() => import('./pages/Store/PaymentSuccessPage'));
const TrackExperiencePage = lazy(() => import('./pages/Store/TrackExperiencePage'));
const ReviewPage = lazy(() => import('./pages/Store/ReviewPage'));
const ProductPage = lazy(() => import('./pages/Store/ProductPage'));
const StaticContentPage = lazy(() => import('./pages/Store/StaticContentPage'));

/** Short URLs like /hulm/contact → /hulm/pages/contact (otherwise /:sellerID/:productID treats "contact" as a product). */
function SubfolderStaticPageRedirect({ slug }) {
  const { sellerID } = useParams();
  if (!sellerID) return null;
  return <Navigate to={`/${sellerID}/pages/${slug}`} replace />;
}

// Context for sellerID (for custom domain support)
export const SellerIDContext = React.createContext(null);



function App() {
  const [sellerID, setSellerID] = useState(null);
  const [isResolving, setIsResolving] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  // Resolve sellerID on mount and when location changes
  useEffect(() => {
    let cancelled = false;

    const prefetchStoreBundles = () =>
      Promise.all([
        import('./pages/Store/Store'),
        import('./store-render/pages/AdvancedStorePage'),
      ]);

    const resolveSeller = async () => {
      setIsResolving(true);
      let normalizedSellerID = null;
      try {
        const resolvedSellerID = await resolveSellerID();

        if (resolvedSellerID) {
          normalizedSellerID = resolvedSellerID.toLowerCase();
          setSellerID(normalizedSellerID);

          const pathParts = location.pathname.split('/').filter(p => p);
          const isCustom = isCustomDomain(getCurrentDomain());

          if (!isCustom && pathParts.length === 0) {
            // Root path on prodinent.com - could show landing or redirect
          }
        } else {
          setSellerID(null);
        }
      } catch (error) {
        setSellerID(null);
      } finally {
        // Load Store + advanced page chunks before hiding the first loader so Suspense
        // does not mount a second full-screen fallback on the next paint.
        if (!cancelled && normalizedSellerID) {
          try {
            await prefetchStoreBundles();
          } catch (e) {
          }
        }
        if (!cancelled) {
          setIsResolving(false);
        }
      }
    };

    resolveSeller();
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  // Show loading while resolving sellerID
  if (isResolving) {
    return <SellerLoader />;
  }

  // If no sellerID and not on root, show error or redirect
  if (!sellerID && location.pathname !== '/') {
    return (
      <ErrorBoundary>
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <h1>Store not found</h1>
          <p>The store you're looking for doesn't exist.</p>
          <button onClick={() => navigate('/')}>Go Home</button>
        </div>
      </ErrorBoundary>
    );
  }

  const isCustom = sellerID && isCustomDomain(getCurrentDomain());
  
  //   sellerID,
  //   isResolving,
  //   isCustom,
  //   pathname: location.pathname,
  //   hostname: window.location.hostname
  // });

  return (
    <HelmetProvider>
      <ErrorBoundary>
        <SellerIDContext.Provider value={sellerID}>
          <MetaPixelBootstrap />
          <WebAnalyticsBootstrap />
          <Suspense fallback={<SellerLoader />}>
            <Routes>
              {/* Root path handling */}
              <Route path="/" element={
                isCustom ? (
                  <Store />
                ) : sellerID ? (
                  <Navigate to={`/${sellerID}`} replace />
                ) : (
                  <div style={{ padding: '40px', textAlign: 'center' }}>
                    <h1>Welcome to Prodinent</h1>
                    <p>Visit a store to get started.</p>
                  </div>
                )
              } />

              {isCustom ? (
                <>
                  {/* Custom domain routes (without sellerID prefix) */}
                  {/* Components will use SellerIDContext to get sellerID */}
                  <Route path="/categories/:categoryID" element={<CategoryPage />} />
                  <Route path="/categories" element={<CategoryPage />} />
                  <Route path="/policies/:policyType" element={<PolicyPage />} />
                  <Route path="/new-customer" element={<NewCustomerPage />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route path="/checkout/:productIDs" element={<CheckoutPage />} />
                  <Route path="/paymentsuccess" element={<PaymentSuccessPage />} />
                  <Route path="/track/:orderID" element={<TrackExperiencePage />} />
                  <Route path="/track" element={<TrackExperiencePage />} />
                  <Route path="/review/:productID" element={<ReviewPage />} />
                  <Route path="/pages/:pageSlug" element={<StaticContentPage />} />
                  <Route path="/contact" element={<Navigate to="/pages/contact" replace />} />
                  <Route path="/about" element={<Navigate to="/pages/about" replace />} />
                  <Route path="/faq" element={<Navigate to="/pages/faq" replace />} />
                  
                  {/* Product routes - must come AFTER reserved routes but BEFORE catch-all */}
                  {/* Custom domain: /productID (no sellerID prefix) */}
                  <Route path="/:productID" element={<ProductPage />} />
                </>
              ) : (
                <>
                  {/* Subfolder-based store routes (with sellerID prefix) */}
                  {/* Store route must come FIRST to match /sellerID before /:productID */}
                  <Route path="/:sellerID" element={<Store />} />
                  <Route path="/:sellerID/categories/:categoryID" element={<CategoryPage />} />
                  <Route path="/:sellerID/categories" element={<CategoryPage />} />
                  <Route path="/:sellerID/policies/:policyType" element={<PolicyPage />} />
                  <Route path="/:sellerID/new-customer" element={<NewCustomerPage />} />
                  <Route path="/:sellerID/checkout" element={<CheckoutPage />} />
                  <Route path="/:sellerID/checkout/:productIDs" element={<CheckoutPage />} />
                    <Route path="/:sellerID/paymentsuccess" element={<PaymentSuccessPage />} />
                  <Route path="/:sellerID/track/:orderID" element={<TrackExperiencePage />} />
                  <Route path="/:sellerID/track" element={<TrackExperiencePage />} />
                  <Route path="/:sellerID/review/:productID" element={<ReviewPage />} />
                  <Route path="/:sellerID/pages/:pageSlug" element={<StaticContentPage />} />
                  <Route path="/:sellerID/contact" element={<SubfolderStaticPageRedirect slug="contact" />} />
                  <Route path="/:sellerID/about" element={<SubfolderStaticPageRedirect slug="about" />} />
                  <Route path="/:sellerID/faq" element={<SubfolderStaticPageRedirect slug="faq" />} />
                  
                  {/* Product route - must come AFTER /:sellerID to match /sellerID/productID */}
                  <Route path="/:sellerID/:productID" element={<ProductPage />} />
                </>
              )}

              {/* Catch all - 404 */}
              <Route path="*" element={
                <div style={{ padding: '40px', textAlign: 'center' }}>
                  <h1>404 - Page not found</h1>
                  <button onClick={() => navigate(sellerID ? (isCustom ? '/' : `/${sellerID}`) : '/')}>
                    Go Back
                  </button>
                </div>
              } />
            </Routes>
          </Suspense>
        </SellerIDContext.Provider>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

// Wrapper component to provide Router
function AppWithRouter() {
  return (
    <Router>
      <App />
    </Router>
  );
}

export default AppWithRouter;
